<template>
  <b-modal
    v-model="show"
    centered
    :title="previewMode ? 'Preview & Save financial record' : 'Create financial record'"
    hide-footer
  >
    <div v-show="previewMode" class="mx-2">
      <div v-if="effectiveExchangeRate" class="mb-3">
        <div>Effective Exchange Rate:</div>
        <strong class="text-primary">{{ effectiveExchangeRate }}</strong>
      </div>
      <div class="mb-3">
        <div>Paid Amount (USD):</div>
        <strong>${{ usdAmount }}</strong>
      </div>
      <div class="mb-3">
        <div>Provider Balance Before Last Purchase:</div>
        <strong>${{ oldBalance }}</strong>
      </div>
      <div class="mb-3">
        <div>Current Provider Balance After Last Purchase:</div>
        <strong>${{ newBalance }}</strong>
      </div>
      <div class="mb-3">
        <div>Provider:</div>
        <strong>{{ getProviderName(providerId) }}</strong>
      </div>
      <div class="mb-3">
        <div>Payment Method:</div>
        <strong>{{ getPaymentMethodName(paymentMethod) }}</strong>
      </div>

      <div class="d-flex flex-column justify-content-center gap-2 mt-3 mb-3">
        <b-button @click.prevent="createFinanceRecord" type="submit" variant="primary" :disabled="loading" class="py-2">
          <b-spinner v-if="loading" small variant="info" />
          <span v-else>Confirm & Save</span>
        </b-button>
        <b-button @click="backToCreationMode" type="button" variant="outline-secondary" :disabled="loading">
          <span>Go Back</span>
        </b-button>
      </div>
    </div>
    <div v-show="!previewMode" class="mx-2">
      <b-form @submit.prevent="previewFinanceRecord">
        <div class="mb-3">
          <label>Paid amount (in USD):</label>
          <b-form-input v-model="usdAmount" type="number" step="0.01" required />
        </div>
        <div class="mb-3">
          <label>Provider balance before last purchase<br />(in provider currency):</label>
          <b-form-input v-model="oldBalance" type="number" step="0.01" required />
        </div>
        <div class="mb-3">
          <label>Current provider balance after last purchase<br />(in provider currency):</label>
          <b-form-input v-model="newBalance" type="number" step="0.01" required />
        </div>
        <div class="mb-3">
          <label>Provider:</label>
          <b-form-select v-model="providerId" :options="providers" required />
        </div>
        <div class="mb-3">
          <label>Payment Method:</label>
          <b-form-select v-model="paymentMethod" :options="paymentMethods" required />
        </div>
        <div class="mb-3">
          <label>Note <small class="text-muted">(not required)</small>:</label>
          <b-form-textarea v-model="note" placeholder="Enter note" rows="2" maxlength="200" />
          <small class="text-muted">{{ noteLength }}/200 characters</small>
        </div>
        <div class="d-flex flex-column justify-content-center mt-3 mb-3">
          <b-button type="submit" variant="primary" :disabled="loading">
            <b-spinner v-if="loading" small variant="info" />
            <span v-else>Preview Financial Record</span>
          </b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import { getAuth } from 'firebase/auth'
import { useActAdminApi } from '@/use/actAdminApi'

export default {
  emits: ['hideModal', 'financeRecordSaved'],
  props: {
    showModal: Boolean,
    providers: {
      type: Array,
      default: () => []
    }
  },
  data: () => {
    return {
      loading: false,
      show: false,
      usdAmount: '',
      oldBalance: '',
      newBalance: '',
      providerId: '',
      paymentMethod: '',
      createdBy: '',
      note: '',
      paymentMethods: [
        { value: 'crypto', text: 'Cryptocurrency' },
        { value: 'card', text: 'Credit Card' },
        { value: 'wire', text: 'Wire transfer' },
        { value: 'paypal', text: 'PayPal' },
        { value: 'other', text: 'Other' }
      ],
      previewMode: false,
      effectiveExchangeRate: null
    }
  },
  computed: {
    noteLength() {
      return this.note ? this.note.length : 0
    }
  },
  watch: {
    showModal() {
      this.show = this.$props.showModal
    },
    show(val) {
      if (!val) {
        this.$emit('hideModal')
        this.usdAmount = this.oldBalance = this.newBalance = this.providerId = this.paymentMethod = ''
        this.previewMode = false
        this.effectiveExchangeRate = null
        this.note = ''
      }
    }
  },
  setup() {
    const { initApiInst, actFinancesApi } = useActAdminApi()
    return { initApiInst, actFinancesApi }
  },
  methods: {
    getProviderName(providerId) {
      const provider = this.providers.find((p) => p.value === providerId)
      return provider ? provider.text : 'Unknown'
    },
    getPaymentMethodName(paymentMethod) {
      const method = this.paymentMethods.find((m) => m.value === paymentMethod)
      return method ? method.text : 'Unknown'
    },
    async previewFinanceRecord() {
      try {
        this.loading = true
        await this.initApiInst()
        const resp = await this.actFinancesApi.previewEffectiveExchangeRate(
          this.usdAmount,
          this.oldBalance,
          this.newBalance,
          this.providerId
        )
        if (resp && resp.effective_exchange_rate) {
          this.effectiveExchangeRate = resp.effective_exchange_rate
          this.previewMode = true
        }
      } catch (e) {
        this.$store.commit('alert/add', {
          id: Date.now(),
          text: 'Finance record preview error! Try again later',
          timeout: 3000,
          type: 'error',
          name: 'error_finance_record_preview'
        })
      } finally {
        this.loading = false
      }
    },
    async createFinanceRecord() {
      try {
        const auth = getAuth()
        const userEmail = auth.currentUser?.email || ''

        const financeRecord = {
          usd_amount: this.usdAmount,
          old_balance: this.oldBalance,
          new_balance: this.newBalance,
          provider_id: this.providerId,
          payment_method: this.paymentMethod,
          created_by: userEmail
        }
        if (this.note) financeRecord.note = this.note

        this.loading = true
        await this.initApiInst()
        const resp = await this.actFinancesApi.createFinanceRecord({ financeRecord })
        if (resp.result && resp.result === 'success') {
          this.$store.commit('alert/add', {
            id: Date.now(),
            text: 'Finance record successfully created',
            timeout: 3000,
            type: 'success',
            name: 'success_finance_record_created'
          })
        }
        this.$emit('financeRecordSaved')
      } catch (e) {
        this.$store.commit('alert/add', {
          id: Date.now(),
          text: 'Finance record saving error! Try again later',
          timeout: 3000,
          type: 'error',
          name: 'error_finance_record_create'
        })
      } finally {
        this.loading = false
      }
    },
    backToCreationMode() {
      this.previewMode = false
      this.effectiveExchangeRate = null
    }
  }
}
</script>
